"use client";

import { SafeHydrate } from "@/SafeHydrate";
import { useState } from "react";
import { ArrowLeft, ArrowRight } from "lucide-react";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
import SectionContainer from "@/components/common/SectionContainer";
const testimonials = [{
  quote: "Paradise Cove was the perfect venue for our wedding. The staff was incredibly helpful and the views are unmatched.",
  author: "Gizem I.",
  role: "Newlywed",
  avatar: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1715425/img/reviewer_gizem_i.png"
}, {
  quote: "We had the most beautiful ceremony by the lake. Everything was seamless from start to finish.",
  author: "Sarah G.",
  role: "Newlywed",
  avatar: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1715425/img/reviewer_sarah_g.webp"
}, {
  quote: "The team at Paradise Cove went above and beyond to make our day special. Highly recommend!",
  author: "Ariana R.",
  role: "Newlywed",
  avatar: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1715425/img/reviewer_ariana_r.png"
}, {
  quote: "As a photographer, I love shooting at Paradise Cove. The light and the scenery are always stunning.",
  author: "Mike Lewis Photography",
  role: "Vendor",
  avatar: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1715425/img/reviewer_mike_lewis.webp"
}];
export default function Testimonials() {
  const [activeIndex, setActiveIndex] = useState(0);
  const {
    ref,
    isVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.15
  });
  const active = testimonials[activeIndex];
  const words = active.quote.split(" ");
  const handlePrev = () => {
    setActiveIndex(prev => prev === 0 ? testimonials.length - 1 : prev - 1);
  };
  const handleNext = () => {
    setActiveIndex(prev => prev === testimonials.length - 1 ? 0 : prev + 1);
  };
  return <section data-section-id="3494" id="words-from-our-couples" className="dark bg-background py-10 md:py-14 lg:py-16">
      <SectionContainer>
        <div className="grid grid-cols-12 gap-6">
          {/* Eyebrow / Coordinate */}
          <div className="col-span-12 mb-6 md:mb-8">
            <span className="font-mono text-[11px] uppercase tracking-[0.2em] text-muted-foreground">
              TESTIMONIALS
            </span>
          </div>

          {/* Active Testimonial */}
          <div ref={ref} className="col-span-12 md:col-span-10 md:col-start-2" key={activeIndex}>
            <blockquote className="relative">
              <span aria-hidden="true" className="absolute -left-4 -top-8 font-serif text-5xl leading-none md:-left-8 md:-top-12 md:text-7xl" style={{
              color: "var(--chart-3)"
            }}>
                &ldquo;
              </span>
              <p className="font-default font-light leading-[1.15] text-foreground" style={{
              fontSize: "clamp(1.75rem, 3.5vw, 2.75rem)",
              letterSpacing: "-0.02em"
            }}>
                {words.map((word, i) => <span data-index={i} key={`${activeIndex}-${i}`} className="inline-block transition-all duration-700 ease-out" style={{
                opacity: isVisible ? 1 : 0,
                transform: isVisible ? "translateY(0)" : "translateY(0.5em)",
                transitionDelay: `${i * 60}ms`
              }}>
                    {word}
                    {i < words.length - 1 && "\u00A0"}
                  </span>)}
                <span aria-hidden="true" className="font-serif" style={{
                color: "var(--chart-3)"
              }}>
                  &rdquo;
                </span>
              </p>
            </blockquote>

            {/* Author info */}
            <div className="mt-8 flex items-center gap-5 md:mt-10">
              <SafeHydrate><Avatar className="size-14 rounded-lg border border-border">
                <AvatarImage src={active.avatar} alt={active.author} className="object-cover" />
                <AvatarFallback className="font-mono text-sm">
                  {active.author.split(" ").map(n => n[0]).join("")}
                </AvatarFallback>
              </Avatar></SafeHydrate>
              <div className="flex flex-col">
                <span className="font-default text-xl text-foreground">
                  {active.author}
                </span>
                <span className="font-mono text-[11px] uppercase tracking-[0.18em] text-muted-foreground mt-1">
                  {active.role}
                </span>
              </div>
            </div>
          </div>

          {/* Controls */}
          <div className="col-span-12 md:col-span-10 md:col-start-2 mt-8 md:mt-10 flex flex-col gap-8 sm:flex-row sm:items-center sm:justify-between">
            {/* Number indicators */}
            <div className="flex items-center gap-4 font-mono text-xs tracking-wider">
              {testimonials.map((_, i) => <Button data-index={i} key={i} onClick={() => setActiveIndex(i)} variant="ghost" className="group flex items-center gap-4 transition-colors duration-300 p-0 h-auto hover:bg-transparent" aria-label={`Show testimonial ${i + 1}`}>
                  <span className={i === activeIndex ? "text-foreground" : "text-muted-foreground/60 hover:text-muted-foreground transition-colors"}>
                    {String(i + 1).padStart(2, "0")}
                  </span>
                  {i < testimonials.length - 1 && <span className="text-muted-foreground/40">/</span>}
                </Button>)}
            </div>

            {/* Prev / Next buttons */}
            <div className="flex items-center gap-3">
              <Button variant="outline" size="icon" onClick={handlePrev} aria-label="Previous testimonial" className="size-10 rounded-lg border-border bg-transparent hover:bg-accent">
                <ArrowLeft className="size-4" style={{
                color: "var(--chart-3)"
              }} />
              </Button>
              <Button variant="outline" size="icon" onClick={handleNext} aria-label="Next testimonial" className="size-10 rounded-lg border-border bg-transparent hover:bg-accent">
                <ArrowRight className="size-4" style={{
                color: "var(--chart-3)"
              }} />
              </Button>
            </div>
          </div>
        </div>
      </SectionContainer>
    </section>;
}